import React, { useState } from 'react';

import { ContentCopy as ContentCopyIcon, Check as CheckIcon } from '@mui/icons-material';
import { Box, Paper, Typography, Divider, Grid, IconButton, Tooltip } from '@mui/material';

interface ClientInfoPaneProps {
  record: {
    clientNumber: string;
    clientName: string;
    responsibleAttorney: string;
    practice: string;
    openDate: string;
    closeDate: string;
  };
}

const ClientInfoPane: React.FC<ClientInfoPaneProps> = ({ record }: ClientInfoPaneProps) => {
  const [copied, setCopied] = useState(false);

  const formatDate = (value: string) => {
    if (!value) return value;
    // Parse date-only strings (YYYY-MM-DD) as local time to avoid a UTC
    // timezone shift that can move the date back a day.
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    const date = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
  };

  const handleCopy = async () => {
    const columns: { label: string; value: string }[] = [
      { label: 'Client Number', value: record.clientNumber },
      { label: 'Client Name', value: record.clientName },
      { label: 'Responsible Attorney', value: record.responsibleAttorney },
      { label: 'Practice / Sub-Practice', value: record.practice },
      { label: 'Open Date', value: formatDate(record.openDate) },
      { label: 'Close Date', value: formatDate(record.closeDate) },
    ];

    const escapeHtml = (value: string) =>
      value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

    const headerCells = columns
      .map(
        (col) =>
          `<th style="border:1px solid #999;padding:6px 10px;background-color:#f2f2f2;text-align:left;font-family:Calibri,Arial,sans-serif;font-size:11pt;">${escapeHtml(
            col.label,
          )}</th>`,
      )
      .join('');
    const valueCells = columns
      .map(
        (col) =>
          `<td style="border:1px solid #999;padding:6px 10px;text-align:left;font-family:Calibri,Arial,sans-serif;font-size:11pt;">${escapeHtml(
            col.value,
          )}</td>`,
      )
      .join('');
    const html = `<table style="border-collapse:collapse;"><thead><tr>${headerCells}</tr></thead><tbody><tr>${valueCells}</tr></tbody></table>`;

    const plainText = `${columns.map((col) => col.label).join('\t')}\n${columns.map((col) => col.value).join('\t')}`;

    try {
      if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([plainText], { type: 'text/plain' }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plainText);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy client information:', error);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" gutterBottom>
          Client Information
        </Typography>
        <Tooltip title={copied ? 'Copied!' : 'Copy as table'}>
          <IconButton onClick={() => void handleCopy()} size="small" aria-label="Copy client information as table">
            {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={3}>
        <Grid>
          <Typography variant="caption" color="text.secondary">
            Client Number
          </Typography>
          <Typography variant="body1">{record.clientNumber}</Typography>
        </Grid>
        <Grid>
          <Typography variant="caption" color="text.secondary">
            Client Name
          </Typography>
          <Typography variant="body1">{record.clientName}</Typography>
        </Grid>
        <Grid>
          <Typography variant="caption" color="text.secondary">
            Responsible Attorney
          </Typography>
          <Typography variant="body1">{record.responsibleAttorney}</Typography>
        </Grid>
        <Grid>
          <Typography variant="caption" color="text.secondary">
            Practice / Sub-Practice
          </Typography>
          <Typography variant="body1">{record.practice}</Typography>
        </Grid>
        <Grid>
          <Typography variant="caption" color="text.secondary">
            Open Date
          </Typography>
          <Typography variant="body1">{formatDate(record.openDate)}</Typography>
        </Grid>
        <Grid>
          <Typography variant="caption" color="text.secondary">
            Close Date
          </Typography>
          <Typography variant="body1">{record.closeDate ? formatDate(record.closeDate) : 'Not Closed'}</Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ClientInfoPane;
